import { Plus, Pencil, Trash2, Import, Loader2 } from 'lucide-react';

/**
 * Source selector for the floor-plan display mode: the immutable scanned
 * room vs. the user's editable floor plans. Renders as a pill row above
 * the 3D viewer, styled after the Images/Floor Plan toggle.
 *
 * @param {Object} props
 * @param {Array} props.plans - Plan summaries from getPlansForRoom
 * @param {number|null} props.activePlanId - null = scanned room
 * @param {boolean} props.editorOpen - Hide mutating actions mid-edit
 * @param {boolean} props.busy - A create/convert request is in flight
 * @param {boolean} props.hasScan - Whether the room has a scan to convert.
 *   Gates the "From Scan" action — hidden until a scan actually exists.
 * @param {Function} props.onSelectScan
 * @param {Function} props.onSelectPlan - (planId) => void
 * @param {Function} props.onNewPlan
 * @param {Function} props.onConvertScan
 * @param {Function} props.onEdit - Edit the active plan
 * @param {Function} props.onDelete - Delete the active plan (confirmed upstream)
 */
const FloorPlanSourceBar = ({
    plans,
    activePlanId,
    editorOpen,
    busy,
    hasScan = false,
    onSelectScan,
    onSelectPlan,
    onNewPlan,
    onConvertScan,
    onEdit,
    onDelete,
}) => {
    const pillClass = (active) => (
        `px-3 py-1.5 rounded-full text-xs font-bold transition-all whitespace-nowrap ${active
            ? 'bg-[var(--primary)] text-[var(--primary-text)]'
            : 'text-[var(--text-muted)] hover:text-[var(--primary)]'
        }`
    );

    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="bg-[var(--bg-hover)] p-1 rounded-full flex gap-1 shadow-sm overflow-x-auto no-scrollbar">
                <button
                    onClick={onSelectScan}
                    disabled={editorOpen}
                    className={pillClass(activePlanId === null)}
                    aria-label="Show the scanned room"
                >
                    Scanned Room
                </button>
                {plans.map((plan) => (
                    <button
                        key={plan.id}
                        onClick={() => onSelectPlan(plan.id)}
                        disabled={editorOpen}
                        className={pillClass(activePlanId === plan.id)}
                        aria-label={`Show floor plan ${plan.name}`}
                        title={plan.source === 'scan_copy' ? 'Converted from scan' : 'Created by hand'}
                    >
                        {plan.name}
                    </button>
                ))}
            </div>

            {!editorOpen && (
                <div className="flex items-center gap-1.5">
                    <button
                        onClick={onNewPlan}
                        disabled={busy}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--primary)] shadow-sm transition disabled:opacity-50"
                        aria-label="Create a new floor plan"
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                        New Plan
                    </button>
                    {hasScan && (
                        <button
                            onClick={onConvertScan}
                            disabled={busy}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--primary)] shadow-sm transition disabled:opacity-50"
                            aria-label="Convert the room scan into an editable plan"
                        >
                            <Import size={14} />
                            From Scan
                        </button>
                    )}
                    {activePlanId !== null && (
                        <>
                            <button
                                onClick={onEdit}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--primary)] shadow-sm transition"
                                aria-label="Edit this floor plan"
                            >
                                <Pencil size={14} />
                                Edit
                            </button>
                            <button
                                onClick={onDelete}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[var(--bg-card)] text-[var(--error)] hover:bg-[var(--error-container)] shadow-sm transition"
                                aria-label="Delete this floor plan"
                            >
                                <Trash2 size={14} />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default FloorPlanSourceBar;
